import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Controller } from '../../../core/domain/controller';
import type { PluginType } from '../../../core/bridge/connect-client';
import type { BackendClient } from '../../bridge/backend-client';
import { overviewCard, statusBadge, statusColor, statusText, discoveryBadge } from '@vitesse/base-ui/components/templates';
import { Plugins } from '@vitesse/proto/vitesse/v1/command_pb.js';
import { Historian } from '@vitesse/proto/vitesse/v1/historian_pb.js';
import { getSession } from '@vitesse/base-ui';
import { fmtDuration } from '@vitesse/base-ui/format';
import { button } from '@vitesse/base-ui/components/base-buttons';
import { t, formatNumber } from '@vitesse/base-ui/i18n';
import { prefetchFirstPage } from '../../shell/symbolGrid/pagePrefetch';
import '@vitesse/base-ui/elements/vitesse-confirm-dialog';

const HOVER_DWELL_MS = 30; // match the nav tree's prefetch dwell

/*
 * <vitesse-controller-card> — the ONE controller card, rendered identically
 * wherever a controller appears (home "all", home "controllers", the gateway
 * detail page). A card is a card: same data, same badges, same affordances,
 * regardless of which view it's seen from. Self-contained per the platform's
 * Self-Contained Component Pattern — it computes its own delete affordance from
 * the gateway's plugin types and owns its own confirm-delete flow, so no view
 * has to re-implement (or forget) it. Light DOM so the agent card SCSS applies.
 *
 * Inputs are the minimum the card needs (not the whole entry): the controller,
 * its gateway id, its description, the gateway's plugin types (for the
 * manual-delete capability), and the backend (for the delete RPC + prefetch).
 */
@customElement('vitesse-controller-card')
export class ControllerCard extends LitElement {
  @property({ attribute: false }) controller?: Controller;
  @property() gatewayId = '';
  /** Friendly gateway label — when set (the flat home views), shows a leading
   *  "which gateway" badge so two same-named controllers (e.g. each gateway's
   *  Core) are distinguishable. Omitted on the gateway detail page (the page is
   *  already scoped to one gateway, so it'd be redundant). */
  @property() gatewayName = '';
  @property() description = '';
  @property({ attribute: false }) pluginTypes: PluginType[] = [];
  @property({ attribute: false }) backend?: BackendClient;
  @state() private confirmOpen = false;
  @state() private deleting = false;
  @state() private deleteError = '';
  /** Earliest recorded historian sample for this controller — its lifespan
   *  as the History tab sees it (so the badge and the chart's range limits
   *  agree). null = unknown or nothing recorded yet → no badge. */
  @state() private recordingSinceMs: number | null = null;
  /** `${gatewayId}/${controllerId}` the current bounds belong to. */
  private boundsKey = '';
  private boundsSeq = 0;
  private hoverTimer?: ReturnType<typeof setTimeout>;

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  /** Manual-delete affordance comes from the owning plugin's advertised
   *  capability — backend-driven, never hardcoded. */
  private get canDelete(): boolean {
    const c = this.controller;
    return !!c && (this.pluginTypes.find((p) => p.pluginId === c.pluginId)?.canManuallyDelete ?? false);
  }

  protected updated(changed: PropertyValues): void {
    // Descriptor updates replace the controller object on every status flip —
    // refetch only when the card points at a DIFFERENT device (or gains a
    // backend), so a flapping PLC doesn't hammer the historian.
    const key = this.controller ? `${this.gatewayId}/${this.controller.id}` : '';
    if (key !== this.boundsKey || changed.has('backend')) {
      this.boundsKey = key;
      void this.fetchBounds();
    }
  }

  /** Worker-cached, sequence-stamped (a slow reply for the previous device
   *  must not land on this one). Skipped outright when the plan doesn't
   *  allow the historian — a badge isn't worth a guaranteed-failing RPC. */
  private async fetchBounds(): Promise<void> {
    const seq = ++this.boundsSeq;
    this.recordingSinceMs = null;
    if (!this.backend || !this.controller || !this.gatewayId) return;
    if (getSession()?.allowsRpc(Historian.method.queryBounds) === false) return;
    const bounds = await this.backend.api.historyBounds(this.gatewayId, this.controller.id);
    if (seq !== this.boundsSeq) return;
    this.recordingSinceMs = bounds?.hasData ? bounds.minTsMs : null;
  }

  private nav(): void {
    if (!this.controller) return;
    this.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { view: 'controller', gatewayId: this.gatewayId, controllerId: this.controller.id },
        bubbles: true,
        composed: true,
      }),
    );
  }

  // Hover → warm the controller's first symbol window so clicking in paints
  // instantly. 30ms dwell skips pass-over hovers.
  private startPrefetch(): void {
    if (!this.backend || !this.controller) return;
    this.cancelPrefetch();
    const id = this.controller.id;
    this.hoverTimer = setTimeout(() => prefetchFirstPage(this.gatewayId, id, this.backend!), HOVER_DWELL_MS);
  }
  private cancelPrefetch(): void {
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = undefined;
    }
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.cancelPrefetch();
  }

  private async confirmDelete(): Promise<void> {
    const c = this.controller;
    if (!c || !this.backend || this.deleting) return;
    this.deleting = true;
    this.deleteError = '';
    try {
      const res = await this.backend.api.removeController(this.gatewayId, c.pluginId, c.id);
      if (res.ok) this.confirmOpen = false; // the card drops via the controllers stream
      else this.deleteError = res.error || 'Could not delete the controller.';
    } catch (err) {
      this.deleteError = err instanceof Error ? err.message : String(err);
    } finally {
      this.deleting = false;
    }
  }

  render() {
    const c = this.controller;
    if (!c) return nothing;
    // The wrapping span stops the delete click bubbling to the card (which would
    // navigate into the controller instead of deleting).
    const actions = this.canDelete
      ? html`<span @click=${(e: Event) => e.stopPropagation()}
          ><vitesse-gated .rpc=${Plugins.method.removeController} inline
            >${button({ label: 'Delete', icon: 'trash-2', color: 'red', size: 'sm', onClick: () => (this.confirmOpen = true) })}</vitesse-gated
          ></span
        >`
      : nothing;
    return html`${overviewCard({
        title: c.name,
        entityType: 'controller',
        actions,
        description: this.description || t('controller.descriptionFallback'),
        clickable: true,
        onClick: () => this.nav(),
        onHover: () => this.startPrefetch(),
        onHoverEnd: () => this.cancelPrefetch(),
        badges: html`${this.gatewayName ? statusBadge('gateway', this.gatewayName, 'hard-drive') : nothing}${statusBadge(statusColor(c.status), t('controller.statusBadge', { status: statusText(c.status) }))}${discoveryBadge(c.discoveryStatus)}${statusBadge('blue', t('controller.symbolCount', { n: formatNumber(c.symbolCount) }))}${
          this.recordingSinceMs === null
            ? nothing
            : statusBadge('gray', t('controller.recordingFor', { duration: fmtDuration(Date.now() - this.recordingSinceMs) }), 'clock')
        }`,
      })}
      ${this.confirmOpen
        ? html`<vitesse-confirm-dialog
            .open=${true}
            heading="Delete controller"
            message=${`Delete "${c.name}"?`}
            detail="Its address is blacklisted from re-discovery until you add it again."
            confirm-label="Delete"
            danger
            ?busy=${this.deleting}
            error=${this.deleteError}
            @confirm=${() => void this.confirmDelete()}
            @dialog-close=${() => (this.confirmOpen = false)}
          ></vitesse-confirm-dialog>`
        : nothing}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-controller-card': ControllerCard;
  }
}
